import { computed } from "vue";

export function useValidation(formData, errors, steps, currentStep) {
  const validateField = (fieldName) => {
    delete errors.value[fieldName];

    switch (fieldName) {
      case "nombres":
      case "apellidos":
        if (!formData[fieldName] || formData[fieldName].trim().length < 2) {
          errors.value[fieldName] = "Debe tener al menos 2 caracteres";
        }
        break;

      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          errors.value[fieldName] = "Email no válido";
        }
        break;

      case "telefonoUSA":
        if (!formData.telefonoUSA) {
          errors.value[fieldName] = "El teléfono es requerido";
        } else {
          const phoneDigits = formData.telefonoUSA.replace(/\D/g, "");
          if (phoneDigits.length !== 10) {
            errors.value[fieldName] = "Teléfono debe tener 10 dígitos";
          }
        }
        break;

      case "zipCode":
        const zipRegex = /^\d{5}(-\d{4})?$/;
        if (!zipRegex.test(formData.zipCode)) {
          errors.value[fieldName] = "Código postal no válido";
        }
        break;

      case "ingresosMensuales":
        const income = parseFloat(formData.ingresosMensuales) || 0;
        if (income <= 0) {
          errors.value[fieldName] = "Los ingresos mensuales son requeridos";
        } else if (income < 500) {
          errors.value[fieldName] = "Los ingresos mínimos son $500";
        } else if (income > 50000) {
          errors.value[fieldName] = "Los ingresos máximos son $50,000";
        }
        break;

      case "montoCredito":
        const loanAmount = parseFloat(formData.montoCredito) || 0;
        if (loanAmount <= 0) {
          errors.value[fieldName] = "El monto del crédito es requerido";
        } else if (loanAmount < 1000) {
          errors.value[fieldName] = "El monto mínimo del crédito es $1,000";
        } else if (loanAmount > 100000) {
          errors.value[fieldName] = "El monto máximo del crédito es $100,000";
        }
        break;
    }
  };

  const validateForm = () => {
    Object.keys(errors.value).forEach((key) => delete errors.value[key]);

    // Validar campos requeridos
    const requiredFields = [
      "nombres",
      "apellidos",
      "email",
      "telefonoUSA",
      "zipCode",
      "tipoDocumento",
    ];
    requiredFields.forEach((field) => {
      validateField(field);
    });

    // Validar archivos requeridos
    const requiredFiles = [
      "documentoFrontal",
      "documentoTrasero",
      "comprobanteDomicilio",
      "selfie",
    ];
    requiredFiles.forEach((field) => {
      if (!formData[field]) {
        errors.value[field] = "Este archivo es requerido";
      }
    });

    // Validar referencias
    const hasUSReference = formData.usReferences.some(
      (ref) => ref.nombre && ref.telefono
    );
    const hasGTReference = formData.guatemalaReferences.some(
      (ref) => ref.nombre && ref.telefono
    );

    if (!hasUSReference) {
      errors.value.usReferences =
        "Debe proporcionar al menos una referencia en USA";
    }

    if (!hasGTReference) {
      errors.value.guatemalaReferences =
        "Debe proporcionar al menos una referencia en Guatemala";
    }

    return Object.keys(errors.value).length === 0;
  };

  const formProgress = computed(() => {
    const requiredFields = [
      "nombres",
      "apellidos",
      "email",
      "telefonoUSA",
      "zipCode",
      "ingresosMensuales",
      "montoCredito",
      "tipoDocumento",
    ];
    const completedFields = requiredFields.filter((field) => {
      if (field === "ingresosMensuales" || field === "montoCredito") {
        return formData[field] > 0;
      }
      return formData[field] && formData[field].trim();
    });

    const fileFields = [
      "documentoFrontal",
      "documentoTrasero",
      "comprobanteDomicilio",
      "selfie",
    ];
    const completedFiles = fileFields.filter((field) => formData[field]);

    const referencesValid =
      formData.usReferences.some((ref) => ref.nombre && ref.telefono) &&
      formData.guatemalaReferences.some((ref) => ref.nombre && ref.telefono);

    const totalRequired = requiredFields.length + fileFields.length + 1; // +1 for references
    const totalCompleted =
      completedFields.length +
      completedFiles.length +
      (referencesValid ? 1 : 0);

    return Math.round((totalCompleted / totalRequired) * 100);
  });

  const stepProgress = computed(() => {
    const currentStepIndex = currentStep.value - 1; // Convert to 0-based index
    if (currentStepIndex < 0 || currentStepIndex >= steps.length) return 0;

    const currentStepObj = steps[currentStepIndex];

    // Si es el paso de confirmación, mostrar 100% de progreso
    if (currentStepObj.isConfirmation) return 100;

    const currentStepFields = currentStepObj.fields;
    let completedFields = 0;

    currentStepFields.forEach((field) => {
      if (field === "usReferences") {
        if (formData.usReferences.some((ref) => ref.nombre && ref.telefono)) {
          completedFields++;
        }
      } else if (field === "guatemalaReferences") {
        if (
          formData.guatemalaReferences.some((ref) => ref.nombre && ref.telefono)
        ) {
          completedFields++;
        }
      } else if (field === "ingresosMensuales" || field === "montoCredito") {
        if (formData[field] > 0) {
          completedFields++;
        }
      } else if (formData[field]) {
        completedFields++;
      }
    });

    return Math.round((completedFields / currentStepFields.length) * 100);
  });

  const canProceedToNextStep = computed(() => {
    const currentStepIndex = currentStep.value - 1; // Convert to 0-based index
    if (currentStepIndex < 0 || currentStepIndex >= steps.length) return false;

    const currentStepObj = steps[currentStepIndex];

    // Si es el paso de confirmación (paso 4), siempre puede proceder
    if (currentStepObj.isConfirmation) return true;

    const currentStepFields = currentStepObj.fields;

    return currentStepFields.every((field) => {
      if (field === "usReferences") {
        return formData.usReferences.some((ref) => ref.nombre && ref.telefono);
      } else if (field === "guatemalaReferences") {
        return formData.guatemalaReferences.some(
          (ref) => ref.nombre && ref.telefono
        );
      } else if (field === "ingresosMensuales" || field === "montoCredito") {
        return formData[field] > 0;
      } else {
        return formData[field] && formData[field].toString().trim();
      }
    });
  });

  const isFormValid = computed(() => {
    return formProgress.value === 100 && Object.keys(errors.value).length === 0;
  });

  return {
    validateField,
    validateForm,
    formProgress,
    stepProgress,
    canProceedToNextStep,
    isFormValid,
  };
}
